import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { attendanceApi, classesApi, studentsApi } from '../../services/api';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, CheckCircle, XCircle, Clock, Download, Save, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const AttendancePage = () => {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      loadStudentsAndAttendance();
    }
  }, [selectedClass, selectedDate]);

  const loadClasses = async () => {
    try {
      const response = await classesApi.getAll();
      setClasses(response.data.classes || []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load classes');
      setLoading(false);
    }
  };

  const loadStudentsAndAttendance = async () => {
    setLoading(true);
    try {
      const [studentsRes, attendanceRes] = await Promise.all([
        studentsApi.getAll({ class_id: selectedClass, limit: 100 }),
        attendanceApi.getAll({
          class_id: selectedClass,
          date: format(selectedDate, 'yyyy-MM-dd')
        })
      ]);

      setStudents(studentsRes.data.students || []);

      // Map existing attendance
      const attendanceMap = {};
      (attendanceRes.data.attendance || []).forEach(a => {
        attendanceMap[a.student_id] = a.status;
      });
      setAttendance(attendanceMap);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleSaveAttendance = async () => {
    if (!selectedClass || Object.keys(attendance).length === 0) {
      toast.error('Please mark attendance for at least one student');
      return;
    }

    setSaving(true);
    try {
      const attendanceData = Object.entries(attendance).map(([student_id, status]) => ({
        student_id,
        status
      }));

      await attendanceApi.createBulk({
        class_id: selectedClass,
        date: format(selectedDate, 'yyyy-MM-dd'),
        attendance: attendanceData
      });

      toast.success('Attendance saved successfully');
    } catch (error) {
      toast.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    if (!selectedClass) {
      toast.error('Please select a class');
      return;
    }

    setExporting(true);
    try {
      const startDate = format(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1), 'yyyy-MM-dd');
      const endDate = format(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0), 'yyyy-MM-dd');

      const response = await attendanceApi.export(selectedClass, startDate, endDate);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_${selectedClass}_${startDate}_to_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Attendance exported successfully');
    } catch (error) {
      toast.error('Failed to export attendance');
    } finally {
      setExporting(false);
    }
  };

  const markAll = (status) => {
    const newAttendance = {};
    students.forEach(s => {
      newAttendance[s.id] = status;
    });
    setAttendance(newAttendance);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'present':
        return <span className="badge-present px-2 py-1 rounded-full text-xs font-medium">Present</span>;
      case 'absent':
        return <span className="badge-absent px-2 py-1 rounded-full text-xs font-medium">Absent</span>;
      case 'late':
        return <span className="badge-late px-2 py-1 rounded-full text-xs font-medium">Late</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">Not marked</span>;
    }
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Attendance</h1>
          <p className="text-muted-foreground mt-1">Mark and manage student attendance</p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleExport}
          disabled={!selectedClass || exporting}
          data-testid="export-attendance-button"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
          Export Excel
        </Button>
      </div>

      {/* Filters */}
      <Card className="border border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger data-testid="select-class">
                  <SelectValue placeholder="Select Class" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map((cls) => (
                    <SelectItem key={cls.id} value={cls.id}>
                      {cls.name} {cls.section && `- ${cls.section}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto" data-testid="select-date">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {format(selectedDate, 'PPP')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {/* Attendance Table */}
      {selectedClass && (
        <Card className="border border-border">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-primary" />
                Mark Attendance - {format(selectedDate, 'MMMM d, yyyy')}
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => markAll('present')}>
                  <CheckCircle className="h-4 w-4 mr-1" />
                  All Present
                </Button>
                <Button variant="outline" size="sm" onClick={() => markAll('absent')}>
                  <XCircle className="h-4 w-4 mr-1" />
                  All Absent
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner h-8 w-8 text-primary" />
              </div>
            ) : students.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Roll No.</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {students.map((student) => (
                        <TableRow key={student.id} className="table-row-hover">
                          <TableCell>{student.roll_number}</TableCell>
                          <TableCell className="font-medium">{student.name}</TableCell>
                          <TableCell>{getStatusBadge(attendance[student.id])}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant={attendance[student.id] === 'present' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handleStatusChange(student.id, 'present')}
                                data-testid={`mark-present-${student.id}`}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant={attendance[student.id] === 'absent' ? 'destructive' : 'outline'}
                                size="sm"
                                onClick={() => handleStatusChange(student.id, 'absent')}
                                data-testid={`mark-absent-${student.id}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant={attendance[student.id] === 'late' ? 'secondary' : 'outline'}
                                size="sm"
                                onClick={() => handleStatusChange(student.id, 'late')}
                                data-testid={`mark-late-${student.id}`}
                              >
                                <Clock className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex justify-end mt-6">
                  <Button onClick={handleSaveAttendance} disabled={saving} data-testid="save-attendance-button">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Attendance
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No students in this class</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AttendancePage;
